import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AiCoachTurnOption, AiCoachTurnPhase } from 'api/AICoachConversationApi';
import { AiCoachConversationEntry, addAiCoachUserChoice } from 'handlers/aiCoachConversation';
import { getAiCoachConversation } from 'selectors/getAiCoachConversation';
import { postAiCoachConversation } from 'thunks';
import { showIntercom } from 'utils/intercom';

export interface UseAiCoachConversationResult {
  entries: AiCoachConversationEntry[];
  options: AiCoachTurnOption[];
  /** True once the conversation has ended. */
  terminal: boolean;
  loading: boolean;
  error: boolean;
  phase?: AiCoachTurnPhase;
  selectOption: (optionId: string) => void;
  retry: () => void;
}

const useAiCoachConversation = (appId?: string): UseAiCoachConversationResult => {
  const dispatch = useDispatch();
  const { entries, options, terminal, phase, isLoading, isError, started, lastOptionId } = useSelector(
    getAiCoachConversation,
  );

  const selectOption = useCallback(
    (optionId: string) => {
      if (!appId || isLoading) return;

      const chosen = options.find((option) => option.id === optionId);
      if (!chosen) return;

      if (chosen.kind === 'intercom') {
        showIntercom();
        return;
      }

      dispatch(addAiCoachUserChoice(chosen.label));
      dispatch(postAiCoachConversation({ appId, optionId }));
    },
    [appId, isLoading, options, dispatch],
  );

  const retry = useCallback(() => {
    if (!appId || isLoading) return;
    dispatch(postAiCoachConversation({ appId, optionId: lastOptionId }));
  }, [appId, isLoading, lastOptionId, dispatch]);

  // Kick off the initial turn only once per conversation. Reopening the modal
  // resumes the in-progress conversation instead of restarting it.
  useEffect(() => {
    if (!appId || started) return;
    dispatch(postAiCoachConversation({ appId }));
  }, [appId, started, dispatch]);

  return { entries, options, terminal, loading: isLoading, error: isError, phase, selectOption, retry };
};

export default useAiCoachConversation;
